// gruppo.ts — il motore della partita a 4 (v013, per Gold): eliminazione. Regole pure, senza rete e senza orologio,
// come partita.ts (l'ora si passa da fuori; chi usa il motore chiama `scadenza()` per sapere quando fare `tick()`).
//
// Fino a 4 posti (p1 è chi organizza). Chi ride è fuori e resta a guardare; vince l'ultimo rimasto; classifica 1°–4°.
//
// Fasi:  attesa → (avvia) conto → gioco → (verifica) → conto breve → gioco … → risultato → (avvia) conto …
//        gioco ⇄ accusa · gioco/conto ⇄ pausa · chi esce durante il round è fuori (gli altri continuano).
//
// Regole della risata (come nell'1 contro 1, adattate al gruppo):
//  - i giocatori ancora in gara stanno in un "anello" (ordine dei posti): ogni telefono analizza sé stesso E il
//    vicino che gli tocca (`guardi`). Così ogni volto è analizzato da due telefoni: il suo e quello di chi lo guarda;
//  - due segnalazioni sulla stessa persona da telefoni diversi entro `finestraConcordanzaMs` = risata confermata;
//  - una segnalazione sola, o il pulsante «Ha riso!» di un altro giocatore in gara = accusa: l'accusato conferma o
//    contesta (senza risposta entro `accusaMs` vale come confermata);
//  - dopo una risata confermata si aspetta `finestraPariMs`: chi ammette (dal suo telefono) di aver riso nello stesso
//    momento esce insieme a lui, a pari posto;
//  - chi nega troppe volte quello che vede il SUO telefono è fuori; chi accusa a vuoto troppe volte non può più accusare;
//  - volto assente per `voltoAssentePausaMs` → pausa; per `voltoAssenteSconfittaMs` → fuori;
//  - scollegato → pausa; non torna entro `riconnessioneMs` → fuori; gli altri continuano;
//  - durata massima del round raggiunta → chi è ancora in gara è primo a pari merito.

import type { ParametriPartita } from "./partita.ts";

export const POSTI_GRUPPO = ["p1", "p2", "p3", "p4"] as const;
export type Posto = (typeof POSTI_GRUPPO)[number];
export const ORGANIZZATORE: Posto = "p1";
export const ePosto = (v: unknown): v is Posto => typeof v === "string" && (POSTI_GRUPPO as readonly string[]).includes(v);
export const numeroPosto = (p: Posto): number => Number(p.slice(1));
export const postoDaNumero = (n: number): Posto | null => (n >= 1 && n <= 4 ? (`p${n}` as Posto) : null);

export type FaseGruppo = "attesa" | "conto" | "gioco" | "verifica" | "accusa" | "pausa" | "risultato";
export type MotivoUscita =
  | "risata" // ha riso
  | "volto" // fuori dall'inquadratura troppo a lungo
  | "scollegato" // non è tornato in tempo
  | "uscita" // ha lasciato la partita
  | "contestazioni"; // ha negato troppe volte quello che vedeva il suo telefono
export type OrigineAccusa = "giocatore" | "analisi";

export interface ParametriGruppo extends ParametriPartita {
  /** Quanti giocatori servono per dare il via (gruppi.minimo_giocatori). */
  minimoGiocatori: number;
  /** Prima del primo via, un posto di chi è scollegato da tanto si libera (gruppi.posto_libero_minuti). */
  postoLiberoMs: number;
}

/** Quando la stanza rinasce (stanza vuota chiusa, istanza cambiata): da dove ripartire. */
export interface RipresaGruppo {
  /** Ultimo round salvato: i nuovi continuano la numerazione. */
  round?: number;
  /** Il primo via c'è già stato (stato del gruppo nel database). */
  iniziato?: boolean;
  /** Primo valore dei contatori dei collegamenti (diverso da quello di prima: le videochiamate vecchie non si confondono). */
  sessioneBase?: number;
  /** Ora della creazione: chi ha il posto ma non è collegato conta come scollegato da qui. */
  adesso?: number;
}

export interface Uscita {
  posizione: number;
  motivo: MotivoUscita;
}

/** Quello che ogni telefono riceve. I campi per posto valgono per tutti e quattro (posto libero: nome ""). */
export interface VistaGruppo {
  versione: number;
  fase: FaseGruppo;
  io: Posto;
  /** Chi può dare il via adesso (l'organizzatore, o il primo collegato se lui non c'è). */
  capo: Posto | null;
  minimo: number;
  occupati: Record<Posto, boolean>;
  nomi: Record<Posto, string>;
  collegati: Record<Posto, boolean>;
  /** Contatore dei collegamenti: la videochiamata tra due telefoni si rifà quando cambia quello di uno dei due. */
  sessioni: Record<Posto, number>;
  pronti: Record<Posto, boolean>;
  volti: Record<Posto, boolean>;
  /** v038b: chi ha spento l'audio (non sente nessuno e nessuno sente lui; gli altri vedono l'icona). */
  muti: Record<Posto, boolean>;
  /** v039: i posti dei bot (solo nella partita di prova di un tester; assente nelle partite vere). */
  bot?: Record<Posto, boolean>;
  /**
   * v042: chi ha detto sì al video delle risate e alle foto della partita. Nel video e nelle foto di ognuno ci sono
   * solo i riquadri di chi ha detto sì (gli altri sono un «?»). I bot della prova dicono sempre sì.
   */
  clip?: Record<Posto, boolean>;
  foto?: Record<Posto, boolean>;
  /** Gioca nel round in corso (o nell'ultimo giocato). */
  inGara: Record<Posto, boolean>;
  /** Fuori nel round in corso: posizione e motivo (null = ancora in gara o non gioca). */
  fuori: Record<Posto, Uscita | null>;
  /** Chi analizza ciascun telefono oltre a sé stesso (il vicino nell'anello). */
  guardi: Record<Posto, Posto | null>;
  round: number;
  tVia: number | null;
  tempoGiocatoMs: number;
  orologioDa: number | null;
  durataMassimaMs: number;
  accusa: { contro: Posto; da: Posto; origine: OrigineAccusa; scade: number } | null;
  pausa: { motivo: "scollegato" | "volto"; chi: Posto; scade: number } | null;
  verifica: { chi: Posto[]; scade: number } | null;
  /** A fine round: posizione di ciascuno che ha giocato (1 = vincitore; a pari merito stesso numero). */
  classifica: { posto: Posto; posizione: number; motivo: MotivoUscita | null }[] | null;
}

export interface RoundGruppoConcluso {
  round: number;
  inizio: number;
  fine: number;
  tempoGiocatoMs: number;
  classifica: { posto: Posto; nome: string; posizione: number; motivo: MotivoUscita | null }[];
}

interface StatoPosto {
  occupato: boolean;
  nome: string;
  collegato: boolean;
  pronto: boolean;
  volto: boolean;
  muto: boolean;
  /** v042: sì al video delle risate e alle foto della partita (come nell'1 contro 1) */
  clip: boolean;
  foto: boolean;
  voltoAssenteDa: number | null;
  scollegatoDa: number | null;
  sessione: number;
  inGara: boolean;
  fuori: Uscita | null;
  contestazioni: number;
  accuseRespinte: number;
}

interface Sospetto {
  su: Posto;
  da: Posto;
  t: number;
  arrivatoAlle: number;
}

const nuovo = (): StatoPosto => ({
  occupato: false,
  nome: "",
  collegato: false,
  pronto: false,
  volto: true,
  muto: false,
  clip: false,
  foto: false,
  voltoAssenteDa: null,
  scollegatoDa: null,
  sessione: 0,
  inGara: false,
  fuori: null,
  contestazioni: 0,
  accuseRespinte: 0,
});

const perPosto = <T>(f: (p: Posto) => T): Record<Posto, T> => ({ p1: f("p1"), p2: f("p2"), p3: f("p3"), p4: f("p4") });

export class MotoreGruppo {
  private fase: FaseGruppo = "attesa";
  private readonly bot = new Set<Posto>();
  private versione = 1;
  private g: Record<Posto, StatoPosto> = perPosto(nuovo);
  private round = 0;
  private tVia: number | null = null;
  private tempoGiocatoMs = 0;
  private orologioDa: number | null = null;
  private inizioRound: number | null = null;
  private sospetti: Sospetto[] = [];
  /** Risate confermate in attesa della finestra del pari: posto → ora. */
  private risate: Partial<Record<Posto, number>> = {};
  private verifica: { scade: number; t: number } | null = null;
  private accusa: { contro: Posto; da: Posto; origine: OrigineAccusa; t: number; scade: number } | null = null;
  private pausa: { motivo: "scollegato" | "volto"; chi: Posto; scade: number } | null = null;
  private classifica: VistaGruppo["classifica"] = null;
  private concluse: RoundGruppoConcluso[] = [];
  /** Il via è già stato dato almeno una volta (dopo, i posti liberi non si riassegnano più). */
  private iniziato = false;
  /** Posti lasciati prima del primo via: chi usa il motore li libera anche nel database. */
  private liberati: Posto[] = [];

  private readonly p: ParametriGruppo;

  constructor(p: ParametriGruppo, nomi: Partial<Record<Posto, string>> = {}, r: RipresaGruppo = {}) {
    this.p = p;
    this.round = r.round ?? 0;
    this.iniziato = r.iniziato ?? false;
    for (const x of POSTI_GRUPPO) this.g[x].sessione = r.sessioneBase ?? 0;
    this.conosciPosti(nomi, r.adesso ?? 0);
  }

  /** Posti presi nel database (anche da chi non si è ancora collegato): occupati, scollegati da `adesso`. */
  conosciPosti(nomi: Partial<Record<Posto, string>>, adesso: number): void {
    let cambiato = false;
    for (const [k, n] of Object.entries(nomi)) {
      if (!ePosto(k) || !n || this.g[k].occupato) continue;
      this.g[k].occupato = true;
      this.g[k].nome = n;
      this.g[k].scollegatoDa = adesso;
      cambiato = true;
    }
    if (cambiato) this.cambiato();
  }

  // --- lettura -----------------------------------------------------------------

  get faseAttuale(): FaseGruppo {
    return this.fase;
  }
  get versioneAttuale(): number {
    return this.versione;
  }
  get giaIniziato(): boolean {
    return this.iniziato;
  }

  /** v039: questi posti sono bot (partita di prova): lo vede la vista, così i telefoni non li chiamano in video. */
  segnaBot(posti: readonly Posto[]): void {
    for (const p of posti) this.bot.add(p);
    if (posti.length) this.cambiato();
  }
  /** v039: in gara adesso (per i bot della partita di prova). */
  inGaraAdesso(p: Posto): boolean {
    return this.inGara(p);
  }
  /** v039: chi analizza il volto di `su` oltre a lui stesso (null se nessuno). */
  osservatore(su: Posto): Posto | null {
    return POSTI_GRUPPO.find((p) => p !== su && this.vicino(p) === su) ?? null;
  }

  /** Il vicino che `p` analizza: il prossimo ancora in gara nell'anello (null se è solo o non è in gara). */
  vicino(p: Posto): Posto | null {
    if (!this.inGara(p)) return null;
    const i = POSTI_GRUPPO.indexOf(p);
    for (let k = 1; k < POSTI_GRUPPO.length; k++) {
      const q = POSTI_GRUPPO[(i + k) % POSTI_GRUPPO.length]!;
      if (this.inGara(q)) return q;
    }
    return null;
  }

  /** Chi dà il via: l'organizzatore se è collegato, se no il primo posto collegato. */
  capo(): Posto | null {
    if (this.g[ORGANIZZATORE].collegato) return ORGANIZZATORE;
    return POSTI_GRUPPO.find((p) => this.g[p].collegato) ?? null;
  }

  vista(io: Posto): VistaGruppo {
    return {
      versione: this.versione,
      fase: this.fase,
      io,
      capo: this.capo(),
      minimo: this.p.minimoGiocatori,
      occupati: perPosto((p) => this.g[p].occupato),
      nomi: perPosto((p) => this.g[p].nome),
      collegati: perPosto((p) => this.g[p].collegato),
      sessioni: perPosto((p) => this.g[p].sessione),
      pronti: perPosto((p) => this.g[p].pronto),
      volti: perPosto((p) => this.g[p].volto),
      muti: perPosto((p) => this.g[p].muto),
      clip: perPosto((p) => this.g[p].occupato && (this.g[p].clip || this.bot.has(p))),
      foto: perPosto((p) => this.g[p].occupato && (this.g[p].foto || this.bot.has(p))),
      ...(this.bot.size ? { bot: perPosto((p) => this.bot.has(p)) } : {}),
      inGara: perPosto((p) => this.g[p].inGara),
      fuori: perPosto((p) => (this.g[p].fuori ? { ...this.g[p].fuori! } : null)),
      guardi: perPosto((p) => (this.inPartita() ? this.vicino(p) : null)),
      round: this.round,
      tVia: this.tVia,
      tempoGiocatoMs: this.tempoGiocatoMs,
      orologioDa: this.orologioDa,
      durataMassimaMs: this.p.durataMassimaMs,
      accusa: this.accusa ? { contro: this.accusa.contro, da: this.accusa.da, origine: this.accusa.origine, scade: this.accusa.scade } : null,
      pausa: this.pausa ? { ...this.pausa } : null,
      verifica: this.verifica ? { chi: POSTI_GRUPPO.filter((p) => this.risate[p] !== undefined), scade: this.verifica.scade } : null,
      classifica: this.classifica ? this.classifica.map((c) => ({ ...c })) : null,
    };
  }

  prendiConcluse(): RoundGruppoConcluso[] {
    const c = this.concluse;
    this.concluse = [];
    return c;
  }

  prendiLiberati(): Posto[] {
    const l = this.liberati;
    this.liberati = [];
    return l;
  }

  scadenza(): number | null {
    const c: number[] = [];
    if (this.fase === "conto" && this.tVia !== null) c.push(this.tVia);
    if (this.fase === "gioco" && this.orologioDa !== null) {
      c.push(this.orologioDa + (this.p.durataMassimaMs - this.tempoGiocatoMs));
      for (const s of this.sospetti) c.push(s.arrivatoAlle + this.p.finestraConcordanzaMs);
      for (const p of this.inGaraOra()) {
        const da = this.g[p].voltoAssenteDa;
        if (da !== null) c.push(da + this.p.voltoAssentePausaMs);
      }
    }
    if (this.fase === "verifica" && this.verifica) c.push(this.verifica.scade);
    if (this.fase === "accusa" && this.accusa) c.push(this.accusa.scade);
    if (this.fase === "pausa" && this.pausa) c.push(this.pausa.scade);
    if (!this.iniziato && this.fase === "attesa") {
      for (const p of POSTI_GRUPPO) {
        const s = this.g[p];
        if (p !== ORGANIZZATORE && s.occupato && !s.collegato && s.scollegatoDa !== null) c.push(s.scollegatoDa + this.p.postoLiberoMs);
      }
    }
    return c.length ? Math.min(...c) : null;
  }

  // --- eventi -------------------------------------------------------------------

  collegato(p: Posto, adesso: number, nome?: string): void {
    const s = this.g[p];
    s.occupato = true;
    if (nome) s.nome = nome;
    s.collegato = true;
    s.scollegatoDa = null;
    s.sessione++;
    if (this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  scollegato(p: Posto, adesso: number): void {
    const s = this.g[p];
    if (!s.collegato) return;
    s.collegato = false;
    s.scollegatoDa = adesso;
    if (this.fase === "attesa" || this.fase === "risultato") s.pronto = false;
    if (this.inGara(p)) {
      if (this.fase === "gioco" || this.fase === "conto") this.mettiInPausa("scollegato", p, adesso, adesso + this.p.riconnessioneMs);
      else if (this.fase === "pausa") this.riprendi(adesso);
    }
    this.cambiato();
  }

  /** v038b: audio spento o riacceso. */
  /** v042: il sì (o il no) al video delle risate o alle foto. Un no vale subito; un sì dal prossimo round. */
  consenso(p: Posto, cosa: "clip" | "foto", valore: boolean): void {
    if (!this.g[p].occupato || this.g[p][cosa] === valore) return;
    this.g[p][cosa] = valore;
    this.cambiato();
  }

  muto(p: Posto, valore: boolean): void {
    if (this.g[p].muto === valore) return;
    this.g[p].muto = valore;
    this.cambiato();
  }

  pronto(p: Posto, valore = true): void {
    if (this.fase !== "attesa" && this.fase !== "risultato") return;
    if (!this.g[p].collegato) return;
    this.g[p].pronto = valore;
    this.cambiato();
  }

  /** Il via (solo `capo()`): servono almeno `minimoGiocatori` collegati e tutti i collegati pronti. */
  avvia(da: Posto, adesso: number): boolean {
    if (this.fase !== "attesa" && this.fase !== "risultato") return false;
    if (this.capo() !== da) return false;
    const collegati = POSTI_GRUPPO.filter((p) => this.g[p].collegato);
    if (collegati.length < Math.max(2, this.p.minimoGiocatori)) return false;
    if (!collegati.every((p) => this.g[p].pronto)) return false;
    this.iniziato = true;
    this.round++;
    for (const p of POSTI_GRUPPO) {
      const s = this.g[p];
      s.inGara = s.collegato;
      s.fuori = null;
      s.pronto = false;
      // Il volto si riparte presente: ogni telefono riavvia l'analisi e dice di nuovo se manca.
      s.volto = true;
      s.voltoAssenteDa = null;
      s.contestazioni = 0;
      s.accuseRespinte = 0;
    }
    this.risate = {};
    this.sospetti = [];
    this.accusa = null;
    this.verifica = null;
    this.pausa = null;
    this.classifica = null;
    this.tempoGiocatoMs = 0;
    this.orologioDa = null;
    this.inizioRound = null;
    this.avviaConto(adesso, this.p.contoMs);
    this.cambiato();
    return true;
  }

  /** Il telefono di `da` ha visto ridere `su` all'ora `t` (ora del server). Vale solo per sé e per il vicino. */
  segnalaRisata(da: Posto, su: Posto, t: number, adesso: number): void {
    if (this.fase !== "gioco" && this.fase !== "verifica") return;
    if (this.tVia === null || !this.inGara(da) || !this.inGara(su)) return;
    if (su !== da && this.vicino(da) !== su) return; // non è il volto che gli tocca
    if (t < this.tVia - this.p.tolleranzaOrarioMs || t > adesso + this.p.tolleranzaOrarioMs) return;
    const tt = Math.min(Math.max(t, this.tVia), adesso);
    if (this.risate[su] !== undefined) return;

    if (this.fase === "verifica") {
      // Si aspetta solo chi ammette di aver riso nello stesso momento (vale solo il suo telefono).
      if (da === su && this.verifica && Math.abs(tt - this.verifica.t) <= this.p.finestraPariMs) {
        this.risate[su] = tt;
        this.cambiato();
      }
      return;
    }
    const gemello = this.sospetti.find((s) => s.su === su && s.da !== da && Math.abs(s.t - tt) <= this.p.finestraConcordanzaMs);
    if (gemello) {
      this.sospetti = this.sospetti.filter((s) => s.su !== su);
      this.confermaRisata(su, Math.min(gemello.t, tt), adesso);
      return;
    }
    this.sospetti = this.sospetti.filter((s) => !(s.su === su && s.da === da));
    this.sospetti.push({ su, da, t: tt, arrivatoAlle: adesso });
    this.cambiato();
  }

  /** Pulsante «Ha riso!»: `da` (in gara) accusa `contro` (in gara). */
  accusaGiocatore(da: Posto, contro: Posto, adesso: number): void {
    if (this.fase !== "gioco" || da === contro || !this.inGara(da) || !this.inGara(contro)) return;
    this.apriAccusa(contro, "giocatore", da, adesso, adesso);
  }

  rispondiAccusa(p: Posto, conferma: boolean, adesso: number): void {
    if (this.fase !== "accusa" || !this.accusa || this.accusa.contro !== p) return;
    const a = this.accusa;
    this.accusa = null;
    if (conferma) {
      this.confermaRisata(p, a.t, adesso);
      return;
    }
    if (a.da === p) {
      this.g[p].contestazioni++;
      if (this.g[p].contestazioni > this.p.contestazioniMassime) {
        this.elimina([p], "contestazioni", adesso);
        this.cambiato();
        return;
      }
    } else {
      this.g[a.da].accuseRespinte++;
    }
    this.sospetti = [];
    this.avviaConto(adesso, this.p.ripresaContoMs);
    this.cambiato();
  }

  volto(p: Posto, presente: boolean, adesso: number): void {
    const s = this.g[p];
    if (s.volto === presente) return;
    s.volto = presente;
    s.voltoAssenteDa = presente ? null : adesso;
    if (presente && this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  /**
   * Uscita volontaria. Prima del primo via il posto si libera (lo può prendere un altro); durante il round chi è in
   * gara è fuori e gli altri continuano.
   */
  esci(p: Posto, adesso: number): void {
    const s = this.g[p];
    if (this.fase === "verifica" && this.verifica && this.inGara(p)) {
      // Prima si chiude la risata appena confermata (chi ha riso esce per primo), poi l'uscita.
      const chi = POSTI_GRUPPO.filter((x) => this.risate[x] !== undefined && x !== p);
      const haRiso = this.risate[p] !== undefined;
      this.verifica = null;
      this.risate = {};
      this.elimina(haRiso ? [...chi, p] : chi, "risata", adesso);
    }
    if (this.inGara(p) && this.inPartita()) {
      if (this.accusa?.contro === p || this.accusa?.da === p) this.accusa = null;
      this.elimina([p], "uscita", adesso);
    }
    s.collegato = false;
    s.pronto = false;
    s.scollegatoDa = adesso;
    if (!this.iniziato && this.fase === "attesa" && p !== ORGANIZZATORE) {
      this.g[p] = { ...nuovo(), sessione: s.sessione };
      this.liberati.push(p);
    }
    if (this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  tick(adesso: number): void {
    for (let giri = 0; giri < 12; giri++) {
      if (!this.passo(adesso)) return;
      this.cambiato();
    }
  }

  // --- interni ------------------------------------------------------------------

  private inGara(p: Posto): boolean {
    return this.g[p].inGara && this.g[p].fuori === null;
  }
  private inGaraOra(): Posto[] {
    return POSTI_GRUPPO.filter((p) => this.inGara(p));
  }
  private inPartita(): boolean {
    return ["conto", "gioco", "verifica", "accusa", "pausa"].includes(this.fase);
  }
  private cambiato(): void {
    this.versione++;
  }

  private passo(adesso: number): boolean {
    if (!this.iniziato && this.fase === "attesa") {
      // Posti di chi è scollegato da troppo prima del primo via: si liberano (non quello di chi organizza).
      const vecchio = POSTI_GRUPPO.find((p) => {
        const s = this.g[p];
        return p !== ORGANIZZATORE && s.occupato && !s.collegato && s.scollegatoDa !== null && adesso >= s.scollegatoDa + this.p.postoLiberoMs;
      });
      if (vecchio) {
        this.g[vecchio] = { ...nuovo(), sessione: this.g[vecchio].sessione };
        this.liberati.push(vecchio);
        return true;
      }
      return false;
    }
    switch (this.fase) {
      case "conto":
        if (this.tVia !== null && adesso >= this.tVia) {
          const assente = this.inGaraOra().find((p) => !this.g[p].collegato);
          if (assente) {
            this.mettiInPausa("scollegato", assente, adesso, (this.g[assente].scollegatoDa ?? adesso) + this.p.riconnessioneMs);
            return true;
          }
          this.fase = "gioco";
          this.orologioDa = this.tVia;
          this.inizioRound ??= this.tVia;
          for (const p of this.inGaraOra()) {
            const da = this.g[p].voltoAssenteDa;
            if (da !== null) this.g[p].voltoAssenteDa = Math.max(da, this.tVia);
          }
          return true;
        }
        return false;
      case "gioco": {
        if (this.orologioDa === null) return false;
        const assente = this.inGaraOra().find((p) => !this.g[p].collegato);
        if (assente) {
          this.mettiInPausa("scollegato", assente, adesso, (this.g[assente].scollegatoDa ?? adesso) + this.p.riconnessioneMs);
          return true;
        }
        const giocato = this.tempoGiocatoMs + (adesso - this.orologioDa);
        if (giocato >= this.p.durataMassimaMs) {
          this.fermaOrologio(adesso);
          this.chiudiRound(adesso);
          return true;
        }
        for (const p of this.inGaraOra()) {
          const da = this.g[p].voltoAssenteDa;
          if (da !== null && adesso - da >= this.p.voltoAssentePausaMs) {
            this.mettiInPausa("volto", p, adesso, da + this.p.voltoAssenteSconfittaMs);
            return true;
          }
        }
        const scaduto = this.sospetti.find((s) => adesso >= s.arrivatoAlle + this.p.finestraConcordanzaMs);
        if (scaduto) {
          this.sospetti = this.sospetti.filter((s) => s !== scaduto && s.su !== scaduto.su);
          this.apriAccusa(scaduto.su, "analisi", scaduto.da, scaduto.t, adesso);
          return true;
        }
        return false;
      }
      case "verifica": {
        if (!this.verifica || adesso < this.verifica.scade) return false;
        this.verifica = null;
        const chi = POSTI_GRUPPO.filter((p) => this.risate[p] !== undefined);
        this.risate = {};
        this.elimina(chi, "risata", adesso);
        return true;
      }
      case "accusa":
        if (this.accusa && adesso >= this.accusa.scade) {
          const a = this.accusa;
          this.accusa = null;
          this.confermaRisata(a.contro, a.t, adesso);
          return true;
        }
        return false;
      case "pausa":
        if (this.pausa && adesso >= this.pausa.scade) {
          const pa = this.pausa;
          this.pausa = null;
          this.fase = "gioco"; // per elimina(): poi si riprende o si chiude
          this.elimina([pa.chi], pa.motivo === "volto" ? "volto" : "scollegato", adesso);
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  private avviaConto(adesso: number, durata: number): void {
    this.fase = "conto";
    this.tVia = adesso + durata;
  }

  private fermaOrologio(adesso: number): void {
    if (this.orologioDa !== null) {
      this.tempoGiocatoMs = Math.min(this.p.durataMassimaMs, this.tempoGiocatoMs + Math.max(0, adesso - this.orologioDa));
      this.orologioDa = null;
    }
  }

  private confermaRisata(p: Posto, t: number, adesso: number): void {
    this.fermaOrologio(adesso);
    this.risate[p] = t;
    this.sospetti = this.sospetti.filter((s) => s.su !== p);
    this.fase = "verifica";
    this.verifica = { scade: adesso + this.p.finestraPariMs, t };
    this.cambiato();
  }

  private apriAccusa(contro: Posto, origine: OrigineAccusa, da: Posto, t: number, adesso: number): void {
    if (this.risate[contro] !== undefined || !this.inGara(contro)) return;
    if (da !== contro && this.g[da].accuseRespinte > this.p.contestazioniMassime) return;
    this.fermaOrologio(adesso);
    this.fase = "accusa";
    this.accusa = { contro, origine, da, t, scade: adesso + this.p.accusaMs };
    this.cambiato();
  }

  /**
   * Mette fuori uno o più giocatori insieme (a pari posto): con A in gara e k fuori insieme, il posto è A − k + 1.
   * Se resta al massimo uno, il round è finito; se no si riprende con il conto breve (l'anello è cambiato).
   */
  private elimina(chi: Posto[], motivo: MotivoUscita, adesso: number): void {
    const fuori = chi.filter((p) => this.inGara(p));
    if (fuori.length) {
      const posizione = this.inGaraOra().length - fuori.length + 1;
      for (const p of fuori) this.g[p].fuori = { posizione, motivo };
    }
    this.sospetti = [];
    if (this.inGaraOra().length <= 1) {
      this.fermaOrologio(adesso);
      this.chiudiRound(adesso);
      return;
    }
    if (this.fase === "conto") return; // uscito durante il conto: il conto continua
    if (this.fase === "verifica" && this.verifica) return; // la risata appena confermata (di altri) si risolve comunque
    if (this.fase === "pausa") {
      this.riprendi(adesso);
      return;
    }
    if (this.fase === "accusa" && this.accusa) return; // l'accusa in corso (su un altro) resta aperta
    this.fermaOrologio(adesso);
    this.accusa = null;
    this.verifica = null;
    this.avviaConto(adesso, this.p.ripresaContoMs);
  }

  private mettiInPausa(motivo: "scollegato" | "volto", chi: Posto, adesso: number, scade: number): void {
    if (this.fase === "pausa") {
      if (this.pausa && scade < this.pausa.scade) this.pausa = { motivo, chi, scade };
      return;
    }
    this.fermaOrologio(adesso);
    this.accusa = null;
    this.verifica = null;
    this.risate = {};
    this.sospetti = [];
    this.pausa = { motivo, chi, scade };
    this.fase = "pausa";
  }

  /** In pausa: se manca ancora qualcuno in gara la pausa passa a lui; se ci sono tutti si riprende col conto breve. */
  private riprendi(adesso: number): void {
    if (this.fase !== "pausa") return;
    const gara = this.inGaraOra();
    const scollegato = gara.find((p) => !this.g[p].collegato);
    if (scollegato) {
      this.pausa = { motivo: "scollegato", chi: scollegato, scade: (this.g[scollegato].scollegatoDa ?? adesso) + this.p.riconnessioneMs };
      return;
    }
    const senzaVolto = gara.find((p) => !this.g[p].volto);
    if (senzaVolto) {
      this.pausa = { motivo: "volto", chi: senzaVolto, scade: (this.g[senzaVolto].voltoAssenteDa ?? adesso) + this.p.voltoAssenteSconfittaMs };
      return;
    }
    this.pausa = null;
    for (const p of gara) this.g[p].voltoAssenteDa = null;
    this.risate = {};
    this.sospetti = [];
    this.avviaConto(adesso, this.p.ripresaContoMs);
  }

  private chiudiRound(adesso: number): void {
    this.fermaOrologio(adesso);
    // Chi è ancora in gara è primo (a pari merito se sono più d'uno: tempo scaduto o risata insieme).
    const giocatori = POSTI_GRUPPO.filter((p) => this.g[p].inGara);
    this.classifica = giocatori
      .map((p) => ({ posto: p, posizione: this.g[p].fuori?.posizione ?? 1, motivo: this.g[p].fuori?.motivo ?? null }))
      .sort((a, b) => a.posizione - b.posizione || numeroPosto(a.posto) - numeroPosto(b.posto));
    this.fase = "risultato";
    this.accusa = null;
    this.verifica = null;
    this.pausa = null;
    this.sospetti = [];
    this.risate = {};
    this.tVia = null;
    for (const p of POSTI_GRUPPO) this.g[p].pronto = false;
    this.concluse.push({
      round: this.round,
      inizio: this.inizioRound ?? adesso,
      fine: adesso,
      tempoGiocatoMs: this.tempoGiocatoMs,
      classifica: this.classifica.map((c) => ({ ...c, nome: this.g[c.posto].nome })),
    });
    this.cambiato();
  }
}
